import { useEffect, useState } from "react";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import {
  Quote,
  Podcast,
  ChevronLeft,
  ChevronRight,
  PenLine,
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Play,
  Pause,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import TributeModal from "@/components/TributeModal";
import AudioPlayer from "@/components/AudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "text", label: "Textes" },
  { key: "audio", label: "Audios" },
];

export default function AudioBar() {
  const [tributes, setTributes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [asc, setAsc] = useState(false);
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });

  useEffect(() => {
    axios.get(`${API}/messages`).then((r) => setTributes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const open = () => setModalOpen(true);
    window.addEventListener("open-tribute-modal", open);
    return () => window.removeEventListener("open-tribute-modal", open);
  }, []);

  const visible = tributes
    .filter((m) => filter === "all" || m.kind === filter)
    .slice()
    .sort((a, b) =>
      asc
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at)
    );

  useEffect(() => {
    if (!emblaApi || visible.length < 2 || paused) return;
    const isMobile = window.innerWidth < 640;
    const t = setInterval(
      () => emblaApi.scrollNext(),
      isMobile ? 30000 : 15000
    );
    return () => clearInterval(t);
  }, [emblaApi, visible.length, paused]);

  useEffect(() => {
    if (!emblaApi) return;
    const on = () => {
      window.dispatchEvent(new Event("memorium-stop-audio"));
      setSelected(emblaApi.selectedScrollSnap());
    };
    emblaApi.on("select", on);
    on();
    return () => emblaApi.off("select", on);
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      window.dispatchEvent(new Event("memorium-stop-audio"));
      emblaApi.reInit();
      emblaApi.scrollTo(0, true);
    }
  }, [visible.length, emblaApi]);

  return (
    <section id="hommage" data-testid="audio-section" className="px-4 py-14 sm:py-20">
      <div className="mx-auto max-w-3xl">
        <Reveal className="mb-8 text-center">
          <h2 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
            Hommages
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500 sm:text-base">
            La voix de toute personne souhaitant laisser un message.
          </p>
        </Reveal>

        <Reveal className="mb-6 flex flex-wrap items-center justify-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`hommage-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                filter === f.key
                  ? "bg-[#1C1917] text-[#FAF8F5]"
                  : "border border-stone-300 text-stone-500 hover:border-[#C5A059]"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            data-testid="hommage-sort-toggle"
            onClick={() => setAsc(!asc)}
            className="flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-1.5 text-xs font-semibold text-stone-500 transition-colors duration-300 hover:border-[#C5A059]"
          >
            {asc ? (
              <ArrowUpNarrowWide className="h-3.5 w-3.5" />
            ) : (
              <ArrowDownWideNarrow className="h-3.5 w-3.5" />
            )}
            {asc ? "Plus anciens" : "Plus récents"}
          </button>
        </Reveal>

        <Reveal>
          {visible.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-stone-300 bg-white/60 p-10 text-center">
              <Quote className="mx-auto h-6 w-6 text-[#C5A059]" />
              <p className="mt-3 text-sm text-stone-500">
                {tributes.length === 0
                  ? "Soyez la première voix à honorer sa mémoire."
                  : "Aucun hommage dans cette catégorie."}
              </p>
              <button
                data-testid="open-tribute-modal"
                onClick={() => setModalOpen(true)}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C85A32]"
              >
                <PenLine className="h-4 w-4" /> Déposer un hommage
              </button>
            </div>
          ) : (
            <>
              <div data-testid="hommage-slider" ref={emblaRef} className="cursor-grab overflow-hidden active:cursor-grabbing">
                <div className="flex">
                  {visible.map((m, i) => (
                    <div key={m.id ?? i} className="min-w-0 flex-[0_0_100%] px-1">
                      <article
                        data-testid={`tribute-slide-${m.id ?? i}`}
                        className="h-full rounded-3xl border border-stone-200/80 bg-white/90 p-6 shadow-sm sm:p-8"
                      >
                        <div className="mb-5 flex flex-wrap items-center gap-2">
                          <span
                            data-testid={`hommage-number-${i}`}
                            className="rounded-full bg-[#C5A059]/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8a6b2e]"
                          >
                            Hommage n°{i + 1} sur {visible.length}
                          </span>
                          {m.kind === "audio" && (
                            <span className="flex items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1 text-[11px] font-medium text-stone-500">
                              <Podcast className="h-3.5 w-3.5" /> Audio • {m.lang}
                            </span>
                          )}
                          <span className="ml-auto text-[11px] text-stone-400">
                            {formatDate(m.created_at)}
                          </span>
                        </div>
                        <h3
                          data-testid={`tribute-name-${i}`}
                          className="font-display text-2xl font-semibold text-stone-900 sm:text-3xl"
                        >
                          {m.name}
                        </h3>
                        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-[#C85A32]">
                          {m.relation || "Proche du Patriarche"}
                        </p>
                        {m.kind === "audio" && m.audio_path ? (
                          <div className="mt-4">
                            <AudioPlayer
                              src={`${API}/files/${m.audio_path}`}
                              testId={`hommage-audio-${m.id ?? i}`}
                            />
                          </div>
                        ) : (
                          <>
                            <Quote className="mt-4 h-4 w-4 text-[#C5A059]" />
                            <p className="mt-3 text-sm leading-relaxed text-stone-600 sm:text-base">
                              {m.message}
                            </p>
                          </>
                        )}
                      </article>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-center gap-6">
                <button
                  data-testid="hommage-prev"
                  onClick={() => emblaApi?.scrollPrev()}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-600 shadow-sm transition-colors duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
                  aria-label="Hommage précédent"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  data-testid="hommage-pause"
                  onClick={() => setPaused(!paused)}
                  className={`flex h-12 w-12 items-center justify-center rounded-full border shadow-md transition-colors duration-300 ${
                    paused
                      ? "border-[#C5A059] bg-[#C5A059] text-[#1C1917]"
                      : "border-stone-300 bg-white text-stone-600 hover:border-[#C5A059] hover:text-[#C5A059]"
                  }`}
                  aria-label={paused ? "Reprendre le défilement" : "Mettre le défilement en pause"}
                  title={paused ? "Reprendre le défilement" : "Mettre en pause"}
                >
                  {paused ? <Play className="ml-0.5 h-5 w-5" /> : <Pause className="h-5 w-5" />}
                </button>
                <button
                  data-testid="hommage-next"
                  onClick={() => emblaApi?.scrollNext()}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-600 shadow-sm transition-colors duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
                  aria-label="Hommage suivant"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5">
                  {visible.map((_, di) => (
                    <button
                      key={di}
                      data-testid={`hommage-dot-${di}`}
                      onClick={() => emblaApi?.scrollTo(di)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        selected === di ? "w-5 bg-[#C5A059]" : "w-1.5 bg-stone-300 hover:bg-stone-400"
                      }`}
                      aria-label={`Aller à l'hommage ${di + 1}`}
                    />
                  ))}
              </div>
              <p className="mt-3 text-center text-[11px] text-stone-400">
                Glissez sur la carte ou utilisez les flèches
              </p>

              <div className="mt-6 text-center">
                <button
                  data-testid="open-tribute-modal"
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C85A32]"
                >
                  <PenLine className="h-4 w-4" /> Déposer un hommage
                </button>
              </div>
            </>
          )}
        </Reveal>
      </div>
      <TributeModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
